import React, { useCallback, useMemo } from 'react';
import { AudioAssistanceButton } from 'view/components/buttons/AudioAssistanceButton';
import AppSidebar from 'view/components/common/Sidebar';
import { MissedWordList } from '../MissedWordList';
import LanguageSelector from 'view/components/common/LanguageSelector';
import { formatDate } from 'utils/utils';
import StudentGradebookEntry from 'models/StudentGradebookEntry';
import MissedWord from 'models/MissedWord';
import contentService from 'services/contentService';
import ActionButton from 'view/components/buttons/ActionButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faEye,
  faGraduationCap,
  faCommentAlt,
  faPlusSquare,
} from '@fortawesome/free-solid-svg-icons';
import useApiCall from 'contexts/ApiCall';
import teacherService from 'services/teacherService';
import AssignmentFeedback, {
  AssignmentFeedbackType,
} from 'models/assignments/AssignmentFeedback';
import 'view/style/student/components/assignment/feedbackSidebar.css';
import AssignmentSubmission from 'models/AssignmentSubmission';
import IconButton from 'view/components/buttons/IconButton';
import { useGradebook } from 'contexts/TeacherGradebookContext';
import FeedbackItem from 'view/components/common/assignment/FeedbackItem';
import StudentQuestion from 'models/StudentQuestion';
import SidebarGradeUpdater from 'view/components/teacher/assignment/grading/SidebarGradeUpdater';
import QuestionResponse from 'models/QuestionResponse';
import Question from 'models/Question';
import { useAssignmentFeedbackContext } from 'contexts/AssignmentFeedbackContext';

interface AssignmentViewerSidebarProps {
  assignment: StudentGradebookEntry;
  missedWords: MissedWord[];
  useDyslexic: boolean;
  activeQuestion?: StudentQuestion;
  onExitAssignment: () => void;
  onViewSettings: () => void;
  onToggleDyslexic: () => void;
  onSelect?: () => void;
  onAddFeedback?: () => void;
  onUpdateQuestion?: (question: Question, response: QuestionResponse) => void;
  teacherView?: boolean;
}

const AssignmentViewerSidebar: React.FC<AssignmentViewerSidebarProps> = ({
  assignment,
  missedWords,
  useDyslexic,
  activeQuestion,
  onExitAssignment,
  onViewSettings,
  onToggleDyslexic,
  onSelect,
  onAddFeedback,
  onUpdateQuestion,
  teacherView = false,
}) => {
  const { feedback, setFeedback } = useAssignmentFeedbackContext();
  const { setGradebook } = useGradebook();
  const makeApiCall = useApiCall();
  const pageContent = contentService.getAssignmentSubmissionPage();

  const handleUpdateGrade = useCallback(
    (grade: number) => {
      if (!teacherView) return;

      let updatedSubmission = AssignmentSubmission.fromAssignmentSubmission(
        assignment.submission,
      );

      if (!grade) return;

      updatedSubmission.grade = grade;

      makeApiCall(teacherService.gradeAssignmentSubmission, updatedSubmission)
        .then((resp) => {
          setGradebook((prev) =>
            prev?.updateAssignmentSubmission(updatedSubmission),
          );
        })
        .catch((error) => alert(error));
    },
    [teacherView, assignment.submission, setGradebook, makeApiCall],
  );

  const handleUpdateQuestionGrade = useCallback(
    (grade: number) => {
      if (
        !teacherView ||
        !activeQuestion ||
        grade === undefined ||
        !onUpdateQuestion
      )
        return;

      let updatedResponse = QuestionResponse.fromQuestionResponse(
        activeQuestion.response,
      );

      updatedResponse.grade = grade;

      onUpdateQuestion(activeQuestion.question, updatedResponse);
    },
    [teacherView, activeQuestion, onUpdateQuestion],
  );

  const handleDeleteFeedback = (feedbackId: string) => {
    setFeedback((prev) =>
      prev?.filter((f) => String(f.id) !== feedbackId.toString()),
    );
  };

  const handleUpdateFeedback = (updatedFeedback: AssignmentFeedback) => {
    if (!feedback) return;
    let originalFeedback = feedback.slice();
    const idx = feedback.findIndex(
      (f) => String(f.id) === updatedFeedback.id?.toString(),
    );
    originalFeedback[idx] = AssignmentFeedback.fromFeedback(updatedFeedback);
    setFeedback([...originalFeedback]);
  };

  const visibleFeedback = useMemo(
    () =>
      feedback.filter(
        (fb) =>
          fb.question?.toString() === activeQuestion?.response.id.toString() &&
          (teacherView ||
            fb.correction_type !== AssignmentFeedbackType.PRIVATE),
      ),
    [teacherView, feedback, activeQuestion?.response.id],
  );

  const submissionGrade = useMemo(
    () => assignment?.submission?.grade,
    [assignment.submission.grade],
  );

  const questionGrade = useMemo(
    () => activeQuestion?.response?.grade,
    [activeQuestion?.response?.grade],
  );

  return (
    <AppSidebar onExit={onExitAssignment} onViewSettings={onViewSettings}>
      <div className="sidebar-content">
        {/* Teacher Feedback Section */}
        {visibleFeedback?.length || teacherView ? (
          <div className="sidebar-feedback-section sidebar-content-data">
            <div className="row spaced">
              <div className="row">
                <FontAwesomeIcon icon={faCommentAlt} />
                <label className="label-small">Feedback</label>
                <AudioAssistanceButton text="feedback" />
              </div>
              {teacherView && (
                <IconButton
                  type="transparent"
                  icon={faPlusSquare}
                  color="var(--dark-green)"
                  onClick={onAddFeedback}
                />
              )}
            </div>

            {visibleFeedback.length > 0 ? (
              <div className="feedback-list">
                {visibleFeedback.map((item, index) => (
                  <FeedbackItem
                    key={`feedback-item-${item.id}`}
                    item={item}
                    writingResponse={assignment.submission.writing_response}
                    teacherView={teacherView}
                    onDelete={handleDeleteFeedback}
                    onUpdate={handleUpdateFeedback}
                  />
                ))}
              </div>
            ) : teacherView ? (
              <div className="no-feedback">No feedback provided yet</div>
            ) : null}
          </div>
        ) : null}

        {/* Grading Section */}
        {teacherView && assignment.submission.id !== '-1' && (
          <>
            <SidebarGradeUpdater
              label="Assignment grade"
              initialGrade={submissionGrade}
              onUpdateGrade={handleUpdateGrade}
            />

            {activeQuestion && (
              <SidebarGradeUpdater
                label="Question grade"
                initialGrade={questionGrade}
                onUpdateGrade={handleUpdateQuestionGrade}
              />
            )}
          </>
        )}

        {/* Due date section */}
        <div className="sidebar-content-data">
          <div className="row">
            <FontAwesomeIcon icon={faCalendar} />
            <label className="label-small">
              {assignment.submission.date_completed
                ? 'Completed on'
                : pageContent.sidebar.due.label}
            </label>
            <AudioAssistanceButton
              text={
                assignment.submission.date_completed
                  ? 'Completed on'
                  : 'Due Date'
              }
            />
          </div>
          <label className="label-normal">
            {assignment.submission.date_completed
              ? formatDate(new Date(assignment.submission.date_completed))
              : assignment.assignment.due_date
              ? formatDate(new Date(assignment.assignment.due_date))
              : 'No due date'}
          </label>
        </div>

        {/* Start Date Section */}
        {assignment.assignment.start_date && (
          <div className="sidebar-content-data">
            <label className="label-small">
              {pageContent.sidebar.available.label}{' '}
              {formatDate(new Date(assignment.assignment.start_date))}
            </label>
          </div>
        )}

        {/* Student-Specific Tools */}
        {!teacherView && <LanguageSelector />}

        {/* Learned Words */}
        <div className="row spaced">
          <div className="row">
            <FontAwesomeIcon icon={faGraduationCap} />
            <label className="label-small">Learned Words</label>
            <AudioAssistanceButton text="Learned words" />
          </div>
        </div>
        <MissedWordList missedWords={missedWords} />

        {/* Dyslexic Font Toggle */}
        {!teacherView && (
          <div className="row spaced">
            <div className="row">
              <FontAwesomeIcon icon={faEye} />
              <label className="label-small">Change to dyslexic font</label>
              <AudioAssistanceButton text="Change to dyslexic font" />
            </div>
            <input
              type="checkbox"
              checked={useDyslexic}
              onChange={onToggleDyslexic}
            />
          </div>
        )}

        {/* Copy Action */}
        {onSelect && <ActionButton label="Create a copy" onClick={onSelect} />}
      </div>
    </AppSidebar>
  );
};

export default AssignmentViewerSidebar;
